import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { Button, type ButtonProps } from './Button.js'

export interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  value: string
  /** What it is, for the label a screen reader reads: "Copy the invitation link". */
  what: string
  children?: React.ReactNode
}

/**
 * Put something on the clipboard and say that it worked.
 *
 * The desk hands out things people cannot retype: a guest instructor's link, a
 * booking reference, a one-time password. **The confirmation is the point** — a
 * copy button that looks identical before and after leaves somebody pressing it
 * three times and pasting nothing.
 */
export function CopyButton({ value, what, children, ...props }: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      variant="secondary"
      size="sm"
      aria-label={copied ? `${what} copied` : `Copy ${what}`}
      leadingIcon={copied ? <Check /> : <Copy />}
      onClick={() => {
        void navigator.clipboard.writeText(value)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
      {...props}
    >
      {children ?? (copied ? 'Copied' : 'Copy')}
    </Button>
  )
}
